/*
 * free-gifts.js — Free gifts in cart logic
 * ------------------------------------------------------
 * Reads config from <script type="application/json" data-free-gifts-config>
 * emitted by snippets/free-gifts-widget.liquid. Subscribes to theme:cart:change
 * and renders sampler/promo tiers based on current cart subtotal. Handles
 * add/remove via Shopify AJAX API, mutual exclusion within promo groups,
 * auto-removal when subtotal drops below a tier threshold, and checkout
 * gating for required tiers.
 *
 * Also drives the prominent shipping progress bar (data-cart-progress-bar)
 * and the checkout-blocked hint that nudges users to pick a gift.
 *
 * Line item properties (underscore = hidden from storefront):
 *   _is_free_gift   = "1"
 *   _free_gift_tier = "s1"|"s2"|"s3"|"p1"|"p2"|"p3"
 *   _free_gift_group = group_id (only for promo tiers)
 *
 * Cart attributes:
 *   _free_gift_declined_<tierId> = "1" when customer declines a tier
 */
(function () {
  'use strict';

  var SELECTORS = {
    widget: '[data-free-gifts-widget]',
    config: '[data-free-gifts-config]',
    samplersSection: '[data-free-gifts-samplers]',
    samplersBody: '[data-free-gifts-samplers-body]',
    samplersTitle: '[data-free-gifts-samplers-title]',
    samplersMeta: '[data-free-gifts-samplers-meta]',
    samplersList: '[data-free-gifts-samplers-list]',
    promoSection: '[data-free-gifts-promo]',
    promoBody: '[data-free-gifts-promo-body]',
    promoTitle: '[data-free-gifts-promo-title]',
    promoMeta: '[data-free-gifts-promo-meta]',
    promoList: '[data-free-gifts-promo-list]',
    decline: '[data-free-gifts-decline]',
    toggle: '[data-free-gifts-toggle]',
    errorBox: '[data-free-gifts-error]',
    progressBar: '[data-cart-progress-bar]',
    checkoutHint: '[data-free-gifts-checkout-hint]'
  };

  var PROP_GIFT = '_is_free_gift';
  var PROP_TIER = '_free_gift_tier';
  var PROP_GROUP = '_free_gift_group';
  var ATTR_DECLINED_PREFIX = '_free_gift_declined_';
  var EVT_CART_CHANGE = 'theme:cart:change';
  var DEBOUNCE_MS = 180;

  var state = {
    cart: null,
    config: null,
    widgets: [],
    progressBars: [],
    busy: false,
    pendingEval: null,
    bound: false,
    ownReload: false,
    firstEvalDone: false,
    prevSamplerMax: 0,
    prevPromoUnlocked: 0
  };

  function $all(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function $(root, sel) { return root.querySelector(sel); }

  function init() {
    var widgetEls = $all(document, SELECTORS.widget);
    state.widgets = [];
    state.progressBars = $all(document, SELECTORS.progressBar);

    widgetEls.forEach(function (w) {
      var cfgEl = w.querySelector(SELECTORS.config);
      if (!cfgEl) return;
      var cfg;
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.warn('[free-gifts] invalid config', e); return; }
      if (!state.config) state.config = cfg;
      state.widgets.push({ root: w, isDrawer: w.dataset.isDrawer === 'true' });
      bindToggleAndDecline(w);
    });

    if (!state.bound) {
      document.addEventListener(EVT_CART_CHANGE, onCartChange);
      document.addEventListener('submit', onFormSubmit, true);
      document.addEventListener('click', onCheckoutClick, true);
      document.addEventListener('click', onHintClick, true);
      state.bound = true;
    }

    fetchCart().then(function () {
      renderProgressBars();
      if (state.widgets.length) return evaluateTiers();
    }).catch(function (err) {
      console.warn('[free-gifts] init fetch failed', err);
    });
  }

  function bindToggleAndDecline(widget) {
    $all(widget, SELECTORS.toggle).forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Locked sections show a progress bar instead of an open/close affordance —
        // ignore clicks while the section has nothing to expand to.
        if (btn.classList.contains('free-gifts-widget__header--locked')) return;
        var which = btn.getAttribute('data-free-gifts-toggle'); // 'samplers' | 'promo'
        var body = widget.querySelector('[data-free-gifts-' + which + '-body]');
        if (!body) return;
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        body.hidden = expanded;
      });
    });
  }

  async function fetchCart() {
    var r = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!r.ok) throw new Error('cart.js ' + r.status);
    state.cart = await r.json();
    return state.cart;
  }

  function onCartChange(e) {
    if (e && e.detail && e.detail.cart) state.cart = e.detail.cart;
    renderProgressBars();
    if (state.pendingEval) clearTimeout(state.pendingEval);
    state.pendingEval = setTimeout(function () {
      evaluateTiers().catch(function (err) { console.warn('[free-gifts] eval failed', err); });
    }, DEBOUNCE_MS);
  }

  function dispatchCartChange() {
    document.dispatchEvent(new CustomEvent(EVT_CART_CHANGE, {
      detail: { cart: state.cart },
      bubbles: true
    }));
  }

  function refreshCart() {
    dispatchCartChange();
    state.ownReload = true;
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  // ---- helpers ----

  function isGiftLine(line) {
    return line && line.properties && line.properties[PROP_GIFT] === '1';
  }

  function eligibleSubtotalCents() {
    if (!state.cart) return 0;
    var giftCents = state.cart.items.filter(isGiftLine).reduce(function (s, l) {
      return s + (l.final_line_price || 0);
    }, 0);
    return Math.max(0, (state.cart.total_price || 0) - giftCents);
  }

  function uahToCents(val) {
    var n = parseInt(String(val).replace(/[^0-9]/g, ''), 10);
    if (isNaN(n)) return 0;
    return n * 100;
  }

  function formatMoneyUah(cents) {
    try {
      var uah = Math.round(cents / 100);
      return uah.toLocaleString('uk-UA') + ' ₴';
    } catch (e) {
      return (cents / 100) + ' ₴';
    }
  }

  function findPromoTierById(tierId) {
    if (!state.config || !state.config.promo) return null;
    var tiers = state.config.promo.tiers || [];
    for (var i = 0; i < tiers.length; i++) if (tiers[i].id === tierId) return tiers[i];
    return null;
  }

  function unlockedSamplerMax(subtotalCents) {
    if (!state.config || !state.config.samplers) return 0;
    var max = 0;
    (state.config.samplers.tiers || []).forEach(function (t) {
      if (subtotalCents >= uahToCents(t.min)) max = Math.max(max, Number(t.max) || 0);
    });
    return max;
  }

  function highestUnlockedSamplerTierId(subtotalCents) {
    if (!state.config || !state.config.samplers) return null;
    var best = null;
    var bestMax = -1;
    (state.config.samplers.tiers || []).forEach(function (t) {
      if (subtotalCents >= uahToCents(t.min) && (Number(t.max) || 0) > bestMax) {
        bestMax = Number(t.max) || 0;
        best = t.id;
      }
    });
    return best;
  }

  function nextSamplerTier(subtotalCents) {
    if (!state.config || !state.config.samplers) return null;
    var ordered = (state.config.samplers.tiers || []).slice().sort(function (a, b) {
      return uahToCents(a.min) - uahToCents(b.min);
    });
    for (var i = 0; i < ordered.length; i++) {
      if (subtotalCents < uahToCents(ordered[i].min)) return ordered[i];
    }
    return null;
  }

  function unlockedPromoTiers(subtotalCents) {
    if (!state.config || !state.config.promo) return [];
    return (state.config.promo.tiers || []).filter(function (t) {
      return subtotalCents >= uahToCents(t.min);
    });
  }

  function nextPromoTier(subtotalCents) {
    if (!state.config || !state.config.promo) return null;
    var ordered = (state.config.promo.tiers || []).slice().sort(function (a, b) {
      return uahToCents(a.min) - uahToCents(b.min);
    });
    for (var i = 0; i < ordered.length; i++) {
      if (subtotalCents < uahToCents(ordered[i].min)) return ordered[i];
    }
    return null;
  }

  function currentSamplerLines() {
    if (!state.cart) return [];
    return state.cart.items.filter(function (l) {
      return isGiftLine(l) && /^s/.test(String(l.properties[PROP_TIER] || ''));
    });
  }

  function currentPromoLineForGroup(groupId) {
    if (!state.cart) return null;
    for (var i = 0; i < state.cart.items.length; i++) {
      var l = state.cart.items[i];
      if (isGiftLine(l) && l.properties[PROP_GROUP] === groupId) return l;
    }
    return null;
  }

  function isDeclined(tierId) {
    if (!state.cart || !state.cart.attributes) return false;
    return String(state.cart.attributes[ATTR_DECLINED_PREFIX + tierId] || '') === '1';
  }

  // ---- AJAX ops ----

  async function addGiftToCart(variantId, tierId, groupId) {
    if (state.busy) return;
    state.busy = true;
    try {
      if (groupId) {
        var existing = state.cart.items.filter(function (l) {
          return isGiftLine(l) && l.properties[PROP_GROUP] === groupId;
        });
        for (var i = 0; i < existing.length; i++) {
          await changeCart(existing[i].key, 0);
        }
      }
      var body = {
        id: variantId,
        quantity: 1,
        properties: {}
      };
      body.properties[PROP_GIFT] = '1';
      body.properties[PROP_TIER] = tierId;
      if (groupId) body.properties[PROP_GROUP] = groupId;
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      await setCartAttribute(ATTR_DECLINED_PREFIX + tierId, '');
      await fetchCart();
      refreshCart();
    } catch (e) {
      console.warn('[free-gifts] addGiftToCart failed', e);
    } finally {
      state.busy = false;
    }
  }

  async function removeGiftFromCart(lineKey) {
    if (state.busy) return;
    state.busy = true;
    try {
      await changeCart(lineKey, 0);
      await fetchCart();
      refreshCart();
    } catch (e) {
      console.warn('[free-gifts] removeGiftFromCart failed', e);
    } finally {
      state.busy = false;
    }
  }

  async function changeCart(lineKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    });
  }

  async function setCartAttribute(name, value) {
    var attrs = {};
    attrs[name] = value;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  async function declineTier(tierId) {
    if (state.busy) return;
    state.busy = true;
    try {
      var existing = state.cart.items.filter(function (l) {
        if (!isGiftLine(l)) return false;
        var t = l.properties[PROP_TIER];
        if (tierId === 'samplers') return /^s/.test(String(t || ''));
        return t === tierId;
      });
      for (var i = 0; i < existing.length; i++) {
        await changeCart(existing[i].key, 0);
      }
      await setCartAttribute(ATTR_DECLINED_PREFIX + tierId, '1');
      await fetchCart();
      refreshCart();
    } catch (e) {
      console.warn('[free-gifts] declineTier failed', e);
    } finally {
      state.busy = false;
    }
  }

  async function clearDecline(tierId) {
    await setCartAttribute(ATTR_DECLINED_PREFIX + tierId, '');
    await fetchCart();
    refreshCart();
  }

  // ---- auto-remove on downgrade ----

  async function autoRemoveLockedGifts(subtotalCents) {
    if (!state.cart) return false;
    var toRemove = [];
    var samplerMax = unlockedSamplerMax(subtotalCents);
    var samplerLines = currentSamplerLines();
    if (samplerLines.length > samplerMax) {
      var extras = samplerLines.slice(samplerMax);
      extras.forEach(function (l) { toRemove.push(l); });
    }
    state.cart.items.filter(isGiftLine).forEach(function (l) {
      var tierId = l.properties[PROP_TIER];
      if (/^p/.test(String(tierId || ''))) {
        var t = findPromoTierById(tierId);
        if (!t || subtotalCents < uahToCents(t.min)) toRemove.push(l);
      }
    });
    if (!toRemove.length) return false;
    state.busy = true;
    try {
      for (var i = 0; i < toRemove.length; i++) {
        await changeCart(toRemove[i].key, 0);
      }
      await fetchCart();
    } finally {
      state.busy = false;
    }
    return true;
  }

  // ---- evaluation ----

  async function evaluateTiers() {
    if (!state.config) return;
    var subtotal = eligibleSubtotalCents();
    var removed = await autoRemoveLockedGifts(subtotal);
    if (removed) subtotal = eligibleSubtotalCents();

    var samplerMax = unlockedSamplerMax(subtotal);
    var promoUnlockedCount = unlockedPromoTiers(subtotal).length;

    renderAll(subtotal);

    // Auto-open: when crossing into a newly unlocked tier (after first eval),
    // expand the relevant section so the customer can pick.
    if (state.firstEvalDone) {
      if (samplerMax > state.prevSamplerMax) autoOpenSection('samplers');
      if (promoUnlockedCount > state.prevPromoUnlocked) autoOpenSection('promo');
    }
    state.prevSamplerMax = samplerMax;
    state.prevPromoUnlocked = promoUnlockedCount;
    state.firstEvalDone = true;
  }

  function autoOpenSection(which) {
    var sectionSel = which === 'samplers' ? SELECTORS.samplersSection : SELECTORS.promoSection;
    var bodySel = which === 'samplers' ? SELECTORS.samplersBody : SELECTORS.promoBody;
    state.widgets.forEach(function (w) {
      var section = w.root.querySelector(sectionSel);
      if (!section || section.hidden) return;
      var btn = section.querySelector(SELECTORS.toggle);
      var body = section.querySelector(bodySel);
      if (btn) btn.setAttribute('aria-expanded', 'true');
      if (body) body.hidden = false;
      section.classList.add('free-gifts-widget__section--just-unlocked');
      setTimeout(function () {
        section.classList.remove('free-gifts-widget__section--just-unlocked');
      }, 1600);
    });
  }

  // ---- rendering ----

  function renderAll(subtotalCents) {
    state.widgets.forEach(function (w) {
      renderSamplers(w, subtotalCents);
      renderPromo(w, subtotalCents);
      toggleWidgetVisibility(w);
    });
    updateCheckoutGate();
  }

  function toggleWidgetVisibility(widget) {
    var samplers = widget.root.querySelector(SELECTORS.samplersSection);
    var promo = widget.root.querySelector(SELECTORS.promoSection);
    var anyVisible = (samplers && !samplers.hidden) || (promo && !promo.hidden);
    widget.root.hidden = !anyVisible;
  }

  function updateCheckoutGate() {
    if (!state.config) return;
    var blocked = !!enforceCheckoutGate();
    var hintText = (state.config.i18n && state.config.i18n.checkout_blocked_hint)
      || 'Add a free gift to your cart to continue';

    document.querySelectorAll('button[name="checkout"], .checkout__button').forEach(function (btn) {
      btn.classList.toggle('checkout__button--gift-blocked', blocked);
      // intentionally do NOT set btn.disabled — we want clicks to fire
      // so we can intercept and animate the gift widget.
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    });

    document.querySelectorAll(SELECTORS.checkoutHint).forEach(function (el) {
      var label = el.querySelector('[data-free-gifts-checkout-hint-text]');
      if (label) label.textContent = hintText;
      el.hidden = !blocked;
      el.classList.toggle('free-gifts-checkout-hint--visible', blocked);
    });
  }

  function renderSamplers(widget, subtotalCents) {
    var section = widget.root.querySelector(SELECTORS.samplersSection);
    if (!section) return;
    var cfg = state.config.samplers;
    if (!cfg || !cfg.enabled || !cfg.products || !cfg.products.length) {
      section.hidden = true;
      return;
    }
    var titleEl = widget.root.querySelector(SELECTORS.samplersTitle);
    var metaEl = widget.root.querySelector(SELECTORS.samplersMeta);
    var listEl = widget.root.querySelector(SELECTORS.samplersList);
    var declineEl = widget.root.querySelector('[data-free-gifts-decline="samplers"]');
    if (!titleEl || !metaEl || !listEl || !declineEl) return;

    var i18n = state.config.i18n || {};
    titleEl.textContent = cfg.title || i18n.samplers_heading || '';

    var samplerMax = unlockedSamplerMax(subtotalCents);
    if (samplerMax <= 0) {
      var next = nextSamplerTier(subtotalCents);
      if (!next) { section.hidden = true; return; }
      var delta = uahToCents(next.min) - subtotalCents;
      metaEl.textContent = (i18n.unlock_at || 'Add {{ amount }} more to unlock').replace('{{ amount }}', formatMoneyUah(delta));
      section.hidden = false;
      listEl.innerHTML = '';
      declineEl.innerHTML = '';
      var toggleBtn = section.querySelector(SELECTORS.toggle);
      var body = section.querySelector(SELECTORS.samplersBody);
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
      if (body) body.hidden = true;
      toggleBtn && toggleBtn.classList.add('free-gifts-widget__header--locked');
      updateSectionProgress(widget, 'samplers', subtotalCents, uahToCents(next.min));
      return;
    }
    section.hidden = false;
    var toggleBtn = section.querySelector(SELECTORS.toggle);
    toggleBtn && toggleBtn.classList.remove('free-gifts-widget__header--locked');
    updateSectionProgress(widget, 'samplers', subtotalCents, 0);

    var currentLines = currentSamplerLines();
    var used = currentLines.length;
    var remaining = Math.max(0, samplerMax - used);

    var meta;
    if (remaining === 1) meta = (i18n.samples_remaining_one || '{{ count }} sample remaining').replace('{{ count }}', '1');
    else if (remaining === 0) meta = (i18n.selected || 'Selected');
    else meta = (i18n.samples_remaining_other || '{{ count }} samples remaining').replace('{{ count }}', String(remaining));
    metaEl.textContent = meta;

    var declined = isDeclined('samplers');
    var mode = cfg.mode || 'optional_decline';
    if (mode === 'optional_decline' || mode === 'required') {
      declineEl.innerHTML = '';
      var declineBtn = document.createElement('button');
      declineBtn.type = 'button';
      declineBtn.className = 'free-gifts-widget__decline-btn' + (declined ? ' free-gifts-widget__decline-btn--active' : '');
      var check = document.createElement('span');
      check.className = 'free-gifts-widget__check' + (declined ? ' free-gifts-widget__check--checked' : '');
      check.innerHTML = declined ? '&#10003;' : '';
      var label = document.createElement('span');
      label.textContent = declined ? (i18n.declined || 'Gift declined') : (i18n.decline || 'Decline gift');
      declineBtn.appendChild(check);
      declineBtn.appendChild(label);
      declineBtn.addEventListener('click', function () {
        if (declined) clearDecline('samplers');
        else declineTier('samplers');
      });
      declineEl.appendChild(declineBtn);
    } else {
      declineEl.innerHTML = '';
    }

    listEl.innerHTML = '';
    cfg.products.forEach(function (p) {
      var isSelected = currentLines.some(function (l) { return String(l.variant_id) === String(p.variant_id); });
      var disabled = !isSelected && (remaining <= 0 || declined);
      var row = buildRow(p, {
        selected: isSelected,
        disabled: disabled || !p.available,
        unavailable: !p.available,
        onClick: function (e) {
          var btn = e.currentTarget;
          if (isSelected) {
            btn.classList.remove('free-gifts-widget__item--selected');
            var chk = btn.querySelector('.free-gifts-widget__check');
            if (chk) { chk.classList.remove('free-gifts-widget__check--checked'); chk.innerHTML = ''; }
            var line = currentLines.find(function (l) { return String(l.variant_id) === String(p.variant_id); });
            if (line) removeGiftFromCart(line.key);
          } else if (!disabled && p.available) {
            btn.classList.add('free-gifts-widget__item--selected');
            var chk = btn.querySelector('.free-gifts-widget__check');
            if (chk) { chk.classList.add('free-gifts-widget__check--checked'); chk.innerHTML = '&#10003;'; }
            var tierId = highestUnlockedSamplerTierId(subtotalCents) || 's1';
            addGiftToCart(p.variant_id, tierId, '');
          }
        }
      });
      listEl.appendChild(row);
    });
  }

  function renderPromo(widget, subtotalCents) {
    var section = widget.root.querySelector(SELECTORS.promoSection);
    if (!section) return;
    var cfg = state.config.promo;
    if (!cfg || !cfg.enabled || !cfg.tiers || !cfg.tiers.length) {
      section.hidden = true;
      return;
    }
    var titleEl = widget.root.querySelector(SELECTORS.promoTitle);
    var metaEl = widget.root.querySelector(SELECTORS.promoMeta);
    var listEl = widget.root.querySelector(SELECTORS.promoList);
    if (!titleEl || !metaEl || !listEl) return;

    var i18n = state.config.i18n || {};
    titleEl.textContent = cfg.title || i18n.promo_heading || '';

    var unlocked = unlockedPromoTiers(subtotalCents);
    if (!unlocked.length) {
      var next = nextPromoTier(subtotalCents);
      if (!next) { section.hidden = true; return; }
      var delta = uahToCents(next.min) - subtotalCents;
      metaEl.textContent = (i18n.unlock_at || 'Add {{ amount }} more to unlock').replace('{{ amount }}', formatMoneyUah(delta));
      section.hidden = false;
      listEl.innerHTML = '';
      var toggleBtn = section.querySelector(SELECTORS.toggle);
      var body = section.querySelector(SELECTORS.promoBody);
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
      if (body) body.hidden = true;
      toggleBtn && toggleBtn.classList.add('free-gifts-widget__header--locked');
      updateSectionProgress(widget, 'promo', subtotalCents, uahToCents(next.min));
      return;
    }
    section.hidden = false;
    var toggleBtn = section.querySelector(SELECTORS.toggle);
    toggleBtn && toggleBtn.classList.remove('free-gifts-widget__header--locked');
    updateSectionProgress(widget, 'promo', subtotalCents, 0);

    metaEl.textContent = i18n.choose_one_of_group || 'Choose one';

    listEl.innerHTML = '';
    unlocked.forEach(function (tier) {
      if (tier.label) {
        var labelRow = document.createElement('div');
        labelRow.className = 'free-gifts-widget__tier-label';
        labelRow.textContent = tier.label;
        listEl.appendChild(labelRow);
      }
      var declinedTier = isDeclined(tier.id);
      var mode = cfg.mode || 'optional';
      if (mode === 'optional_decline' || mode === 'required') {
        var declineBtn = document.createElement('button');
        declineBtn.type = 'button';
        declineBtn.className = 'free-gifts-widget__decline-btn' + (declinedTier ? ' free-gifts-widget__decline-btn--active' : '');
        var check = document.createElement('span');
        check.className = 'free-gifts-widget__check' + (declinedTier ? ' free-gifts-widget__check--checked' : '');
        check.innerHTML = declinedTier ? '&#10003;' : '';
        var lbl = document.createElement('span');
        lbl.textContent = declinedTier ? (i18n.declined || 'Gift declined') : (i18n.decline || 'Decline gift');
        declineBtn.appendChild(check);
        declineBtn.appendChild(lbl);
        declineBtn.addEventListener('click', function () {
          if (declinedTier) clearDecline(tier.id);
          else declineTier(tier.id);
        });
        listEl.appendChild(declineBtn);
      }
      var currentLine = currentPromoLineForGroup(tier.group_id);
      (tier.products || []).forEach(function (p) {
        var isSelected = !!(currentLine && String(currentLine.variant_id) === String(p.variant_id));
        var otherSelected = !isSelected && !!currentLine;
        var row = buildPromoRow(p, {
          selected: isSelected,
          disabled: otherSelected || !p.available || declinedTier,
          unavailable: !p.available,
          onClick: function (e) {
            var addBtn = e.currentTarget;
            var rowEl = addBtn.parentNode;
            if (isSelected) {
              rowEl.classList.remove('free-gifts-widget__promo-item--selected');
              addBtn.classList.remove('free-gifts-widget__promo-btn--added');
              addBtn.textContent = (state.config.i18n || {}).select || 'Add';
              if (currentLine) removeGiftFromCart(currentLine.key);
            } else if (!otherSelected && p.available && !declinedTier) {
              rowEl.classList.add('free-gifts-widget__promo-item--selected');
              addBtn.classList.add('free-gifts-widget__promo-btn--added');
              addBtn.textContent = (state.config.i18n || {}).selected || 'Added';
              addGiftToCart(p.variant_id, tier.id, tier.group_id);
            }
          }
        });
        listEl.appendChild(row);
      });
    });
  }

  function updateSectionProgress(widget, which, subtotalCents, targetCents) {
    var bar = widget.root.querySelector('[data-free-gifts-' + which + '-progress]');
    var fill = widget.root.querySelector('[data-free-gifts-' + which + '-progress-fill]');
    if (!bar || !fill) return;
    if (!targetCents || targetCents <= 0) {
      bar.hidden = true;
      fill.style.width = '0%';
      return;
    }
    var pct = Math.max(0, Math.min(100, Math.round((subtotalCents / targetCents) * 100)));
    bar.hidden = false;
    fill.style.width = pct + '%';
  }

  function buildRow(product, opts) {
    var row = document.createElement('button');
    row.type = 'button';
    row.className = 'free-gifts-widget__item' +
      (opts.selected ? ' free-gifts-widget__item--selected' : '') +
      (opts.disabled ? ' free-gifts-widget__item--disabled' : '');
    row.setAttribute('data-variant-id', String(product.variant_id));
    if (opts.disabled) row.setAttribute('aria-disabled', 'true');

    var check = document.createElement('span');
    check.className = 'free-gifts-widget__check' + (opts.selected ? ' free-gifts-widget__check--checked' : '');
    check.innerHTML = opts.selected ? '&#10003;' : '';
    row.appendChild(check);

    var thumb = document.createElement('img');
    thumb.className = 'free-gifts-widget__thumb';
    thumb.src = product.image || '';
    thumb.alt = product.title || '';
    thumb.loading = 'lazy';
    row.appendChild(thumb);

    var text = document.createElement('span');
    text.className = 'free-gifts-widget__text';
    if (product.brand) {
      var brand = document.createElement('span');
      brand.className = 'free-gifts-widget__brand';
      brand.textContent = product.brand;
      text.appendChild(brand);
    }
    var desc = document.createElement('span');
    desc.className = 'free-gifts-widget__desc';
    desc.textContent = product.title || '';
    text.appendChild(desc);
    if (opts.unavailable) {
      var un = document.createElement('span');
      un.className = 'free-gifts-widget__unavailable';
      un.textContent = (state.config.i18n && state.config.i18n.unavailable) || 'Unavailable';
      text.appendChild(un);
    }
    row.appendChild(text);

    if (opts.onClick) row.addEventListener('click', opts.onClick);
    return row;
  }

  function buildPromoRow(product, opts) {
    var i18n = state.config.i18n || {};
    var row = document.createElement('div');
    row.className = 'free-gifts-widget__promo-item' +
      (opts.selected ? ' free-gifts-widget__promo-item--selected' : '') +
      (opts.disabled ? ' free-gifts-widget__promo-item--disabled' : '');
    row.setAttribute('data-variant-id', String(product.variant_id));

    var thumb = document.createElement('img');
    thumb.className = 'free-gifts-widget__thumb';
    thumb.src = product.image || '';
    thumb.alt = product.title || '';
    thumb.loading = 'lazy';
    row.appendChild(thumb);

    var text = document.createElement('span');
    text.className = 'free-gifts-widget__text';
    if (product.brand) {
      var brand = document.createElement('span');
      brand.className = 'free-gifts-widget__brand';
      brand.textContent = product.brand;
      text.appendChild(brand);
    }
    var desc = document.createElement('span');
    desc.className = 'free-gifts-widget__desc';
    desc.textContent = product.title || '';
    text.appendChild(desc);
    if (opts.unavailable) {
      var un = document.createElement('span');
      un.className = 'free-gifts-widget__unavailable';
      un.textContent = i18n.unavailable || 'Unavailable';
      text.appendChild(un);
    }
    row.appendChild(text);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'free-gifts-widget__promo-btn' + (opts.selected ? ' free-gifts-widget__promo-btn--added' : '');
    if (opts.disabled && !opts.selected) btn.disabled = true;
    btn.textContent = opts.selected ? (i18n.selected || 'Added') : (i18n.select || 'Add');
    if (opts.onClick) btn.addEventListener('click', opts.onClick);
    row.appendChild(btn);

    return row;
  }

  // ---- shipping progress bar ----

  function renderProgressBars() {
    state.progressBars = $all(document, SELECTORS.progressBar);
    if (!state.progressBars.length) return;
    var totalCents = (state.cart && state.cart.total_price) || 0;
    state.progressBars.forEach(function (bar) {
      var limitUah = parseInt(bar.getAttribute('data-limit') || '0', 10);
      if (!limitUah) return;
      var limitCents = limitUah * 100;
      var pct = limitCents ? Math.min(100, Math.round((totalCents / limitCents) * 100)) : 0;
      var fill = bar.querySelector('[data-cart-progress-bar-fill]');
      if (fill) fill.style.width = pct + '%';
      bar.setAttribute('data-percent', String(pct));
      bar.classList.toggle('cart__progress-bar--complete', totalCents >= limitCents && totalCents > 0);

      var leftEl = bar.querySelector('[data-left-to-spend]');
      if (leftEl) {
        var leftCents = Math.max(0, limitCents - totalCents);
        leftEl.textContent = formatMoneyUah(leftCents);
      }
    });
  }

  // ---- checkout gate ----

  function enforceCheckoutGate() {
    if (!state.config) return null;
    var subtotal = eligibleSubtotalCents();
    var i18n = state.config.i18n || {};

    if (state.config.samplers && state.config.samplers.enabled && state.config.samplers.mode === 'required') {
      var samplerMax = unlockedSamplerMax(subtotal);
      if (samplerMax > 0) {
        var picked = currentSamplerLines().length > 0;
        var declined = isDeclined('samplers');
        if (!picked && !declined) return i18n.error_required || 'Please select a free gift or decline it before checkout.';
      }
    }

    if (state.config.promo && state.config.promo.enabled && state.config.promo.mode === 'required') {
      var tiers = unlockedPromoTiers(subtotal);
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i];
        var hasPick = !!currentPromoLineForGroup(t.group_id);
        var declinedP = isDeclined(t.id);
        if (!hasPick && !declinedP) return i18n.error_required || 'Please select a free gift or decline it before checkout.';
      }
    }
    return null;
  }

  function onFormSubmit(e) {
    var form = e.target;
    if (!form || !(form.matches && (form.matches('form.cart') || form.id === 'cart-form'))) return;
    if (state.busy) {
      e.preventDefault();
      return;
    }
    var err = enforceCheckoutGate();
    if (err) {
      e.preventDefault();
      e.stopPropagation();
      blockedFeedback(err);
    }
  }

  function onCheckoutClick(e) {
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"], .checkout__button');
    if (!btn) return;
    if (!btn.classList.contains('checkout__button--gift-blocked')) return;
    var err = enforceCheckoutGate();
    if (!err) return;
    e.preventDefault();
    e.stopPropagation();
    blockedFeedback(err);
  }

  function onHintClick(e) {
    var hint = e.target && e.target.closest && e.target.closest(SELECTORS.checkoutHint);
    if (!hint || hint.hidden) return;
    e.preventDefault();
    e.stopPropagation();

    // Play the "drag-up" animation on the hint itself, then trigger the
    // same blocked-feedback flow (expand sections + shake + scroll).
    hint.classList.remove('free-gifts-checkout-hint--drag');
    void hint.offsetWidth; // restart
    hint.classList.add('free-gifts-checkout-hint--drag');

    var err = enforceCheckoutGate() || ((state.config && state.config.i18n && state.config.i18n.checkout_blocked_hint) || '');
    // Slight delay so the drag motion reads before the section expands.
    setTimeout(function () { blockedFeedback(err); }, 220);
  }

  function blockedFeedback(err) {
    // Open whichever sections are unlocked but un-picked so the user can act.
    var subtotal = eligibleSubtotalCents();
    var openSamplers = false, openPromo = false;
    if (state.config.samplers && state.config.samplers.enabled && state.config.samplers.mode === 'required') {
      if (unlockedSamplerMax(subtotal) > 0 && currentSamplerLines().length === 0 && !isDeclined('samplers')) {
        openSamplers = true;
      }
    }
    if (state.config.promo && state.config.promo.enabled && state.config.promo.mode === 'required') {
      var tiers = unlockedPromoTiers(subtotal);
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i];
        if (!currentPromoLineForGroup(t.group_id) && !isDeclined(t.id)) { openPromo = true; break; }
      }
    }

    state.widgets.forEach(function (w) {
      if (openSamplers) expandSection(w, 'samplers');
      if (openPromo) expandSection(w, 'promo');
    });

    document.querySelectorAll(SELECTORS.checkoutHint).forEach(function (hint) {
      hint.classList.remove('free-gifts-checkout-hint--bump');
      void hint.offsetWidth; // restart animation
      hint.classList.add('free-gifts-checkout-hint--bump');
    });
    showError(err);
  }

  function expandSection(widget, which) {
    var sectionSel = which === 'samplers' ? SELECTORS.samplersSection : SELECTORS.promoSection;
    var bodySel = which === 'samplers' ? SELECTORS.samplersBody : SELECTORS.promoBody;
    var section = widget.root.querySelector(sectionSel);
    if (!section || section.hidden) return;
    var btn = section.querySelector(SELECTORS.toggle);
    var body = section.querySelector(bodySel);
    if (btn) btn.setAttribute('aria-expanded', 'true');
    if (body) body.hidden = false;
    section.classList.add('free-gifts-widget__section--pulse');
    setTimeout(function () {
      section.classList.remove('free-gifts-widget__section--pulse');
    }, 1600);
  }

  function showError(msg) {
    state.widgets.forEach(function (w) {
      var el = w.root.querySelector(SELECTORS.errorBox);
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
      w.root.classList.add('free-gifts-widget--error-shake');
      setTimeout(function () { w.root.classList.remove('free-gifts-widget--error-shake'); }, 500);
      setTimeout(function () { el.hidden = true; }, 6000);
      try {
        w.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e2) {}
    });
  }

  // ---- boot ----

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('theme:cart:reload', function () {
    if (state.ownReload) { state.ownReload = false; return; }
    init();
  });

  window.FreeGifts = {
    evaluate: evaluateTiers,
    state: state,
    enforceCheckoutGate: enforceCheckoutGate,
    renderProgressBars: renderProgressBars
  };
})();
